/**
 * Dashboard Application - Multi-site Task Automation Platform
 * Optimized with Supabase Realtime and smart polling
 */

// Import functions from other modules
import { initSupabase, getSupabase } from './supabase-client.js';
import {
    initSiteSwitcher,
    getCurrentSite,
    getSiteConfig,
    getAllSiteConfigs,
    updateSiteBadge,
    getFormData,
    renderTaskItem,
    renderGallery,
    updateWorkerCache
} from './site-switcher.js';

// Global functions for HTML onclick handlers
window.forceRecoverAllTasks = forceRecoverAllTasks;
window.resetTask = resetTask;
window.tryRecoverTask = tryRecoverTask;
window.openRetryModal = openRetryModal;

// Configuration
const CONFIG = {
    FALLBACK_POLL_INTERVAL: 30000,  // 30초 (Realtime 실패 시 폴백)
    STATS_POLL_INTERVAL: 60000,     // 60초 (통계는 덜 자주)
    GALLERY_LIMIT: 12,              // 갤러리 이미지 수 감소
    DEBOUNCE_DELAY: 500             // 디바운스 딜레이
};

// State
let state = {
    currentPage: 1,
    pageSize: 20,
    totalPages: 1,
    statusFilter: 'all',
    stats: {
        pending: 0,
        claimed: 0,
        processing: 0,
        completed: 0,
        failed: 0
    },
    allSitesStats: {},
    isPageVisible: true,
    realtimeChannels: [],
    fallbackPollInterval: null,
    statsPollInterval: null,
    isAuthenticated: false
};

// DOM Elements
const elements = {
    // Auth screens
    loginScreen: document.getElementById('login-screen'),
    signupScreen: document.getElementById('signup-screen'),
    appMain: document.getElementById('app-main'),
    
    // Login form
    loginForm: document.getElementById('login-form'),
    loginEmail: document.getElementById('login-email'),
    loginPassword: document.getElementById('login-password'),
    loginSubmitBtn: document.getElementById('login-submit-btn'),
    loginError: document.getElementById('login-error'),
    showSignupBtn: document.getElementById('show-signup'),
    
    // Signup form
    signupForm: document.getElementById('signup-form'),
    signupEmail: document.getElementById('signup-email'),
    signupPassword: document.getElementById('signup-password'),
    signupPasswordConfirm: document.getElementById('signup-password-confirm'),
    signupSubmitBtn: document.getElementById('signup-submit-btn'),
    signupError: document.getElementById('signup-error'),
    signupSuccess: document.getElementById('signup-success'),
    showLoginBtn: document.getElementById('show-login'),
    
    // User info / logout
    userInfo: document.getElementById('user-info'),
    userEmail: document.getElementById('user-email'),
    logoutBtn: document.getElementById('logout-btn'),

    // Stats
    statPending: document.getElementById('stat-pending'),
    statClaimed: document.getElementById('stat-claimed'),
    statProcessing: document.getElementById('stat-processing'),
    statCompleted: document.getElementById('stat-completed'),
    statFailed: document.getElementById('stat-failed'),

    // Connection
    connectionStatus: document.getElementById('connection-status'),

    // Add tasks
    addTaskBtn: document.getElementById('add-task-btn'),
    addResult: document.getElementById('add-result'),

    // Workers
    workersList: document.getElementById('workers-list'),

    // Tasks
    tasksList: document.getElementById('tasks-list'),
    statusFilter: document.getElementById('status-filter'),
    refreshBtn: document.getElementById('refresh-btn'),
    prevPage: document.getElementById('prev-page'),
    nextPage: document.getElementById('next-page'),
    pageInfo: document.getElementById('page-info'),

    // Gallery
    gallery: document.getElementById('gallery'),
    downloadAllBtn: document.getElementById('download-all-btn'),

    // Settings
    settingsBtn: document.getElementById('settings-btn'),
    settingsModal: document.getElementById('settings-modal'),
    closeModal: document.getElementById('close-modal'),
    supabaseUrl: document.getElementById('supabase-url'),
    supabaseKey: document.getElementById('supabase-key'),
    saveSettings: document.getElementById('save-settings')
};

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    initSiteSwitcher();
    setupAuthEventListeners();
    loadSettings();
});

/**
 * Setup authentication-related event listeners
 */
function setupAuthEventListeners() {
    // Login form submit
    if (elements.loginForm) {
        elements.loginForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            await handleLogin();
        });
    }
    
    // Signup form submit
    if (elements.signupForm) {
        elements.signupForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            await handleSignup();
        });
    }
    
    // Switch to signup
    if (elements.showSignupBtn) {
        elements.showSignupBtn.addEventListener('click', () => {
            elements.loginScreen.classList.add('hidden');
            elements.signupScreen.classList.remove('hidden');
            elements.signupError.textContent = '';
            elements.signupSuccess.textContent = '';
        });
    }
    
    // Switch to login
    if (elements.showLoginBtn) {
        elements.showLoginBtn.addEventListener('click', () => {
            elements.signupScreen.classList.add('hidden');
            elements.loginScreen.classList.remove('hidden');
            elements.loginError.textContent = '';
        });
    }
    
    // Logout button
    if (elements.logoutBtn) {
        elements.logoutBtn.addEventListener('click', async () => {
            await handleLogout();
        });
    }
}

/**
 * Handle login form submission
 */
async function handleLogin() {
    const email = elements.loginEmail.value.trim();
    const password = elements.loginPassword.value;
    
    if (!email || !password) {
        elements.loginError.textContent = '이메일과 비밀번호를 입력해주세요.';
        return;
    }
    
    elements.loginSubmitBtn.disabled = true;
    elements.loginSubmitBtn.textContent = '로그인 중...';
    elements.loginError.textContent = '';
    
    try {
        const supabase = getSupabase();
        if (!supabase) {
            elements.loginError.textContent = 'Supabase 연결이 필요합니다.';
            return;
        }
        
        const { user, error } = await supabase.signInWithPassword(email, password);
        
        if (error) {
            elements.loginError.textContent = error;
            return;
        }
        
        // Login successful - show app
        showAuthenticatedApp(user);
    } catch (err) {
        elements.loginError.textContent = '로그인 실패: ' + err.message;
    } finally {
        elements.loginSubmitBtn.disabled = false;
        elements.loginSubmitBtn.textContent = '로그인';
    }
}

/**
 * Handle signup form submission
 */
async function handleSignup() {
    const email = elements.signupEmail.value.trim();
    const password = elements.signupPassword.value;
    const passwordConfirm = elements.signupPasswordConfirm.value;
    
    if (!email || !password) {
        elements.signupError.textContent = '이메일과 비밀번호를 입력해주세요.';
        return;
    }
    
    if (password !== passwordConfirm) {
        elements.signupError.textContent = '비밀번호가 일치하지 않습니다.';
        return;
    }
    
    if (password.length < 6) {
        elements.signupError.textContent = '비밀번호는 최소 6자 이상이어야 합니다.';
        return;
    }
    
    elements.signupSubmitBtn.disabled = true;
    elements.signupSubmitBtn.textContent = '가입 중...';
    elements.signupError.textContent = '';
    elements.signupSuccess.textContent = '';
    
    try {
        const supabase = getSupabase();
        if (!supabase) {
            elements.signupError.textContent = 'Supabase 연결이 필요합니다.';
            return;
        }
        
        const { user, session, error } = await supabase.signUp(email, password);
        
        if (error) {
            elements.signupError.textContent = error;
            return;
        }
        
        // Check if email confirmation is required
        if (session) {
            // Immediately logged in
            showAuthenticatedApp(user);
        } else {
            // Email confirmation required
            elements.signupSuccess.textContent = '가입 완료! 이메일을 확인하여 계정을 활성화해주세요.';
            elements.signupEmail.value = '';
            elements.signupPassword.value = '';
            elements.signupPasswordConfirm.value = '';
        }
    } catch (err) {
        elements.signupError.textContent = '가입 실패: ' + err.message;
    } finally {
        elements.signupSubmitBtn.disabled = false;
        elements.signupSubmitBtn.textContent = '가입하기';
    }
}

/**
 * Handle logout
 */
async function handleLogout() {
    try {
        const supabase = getSupabase();
        if (supabase) {
            // Cleanup realtime subscriptions before logout
            cleanupRealtimeSubscriptions();
            supabase.disconnectRealtime();
            await supabase.signOut();
        }
        
        // Show login screen
        showLoginScreen();
    } catch (err) {
        console.error('Logout error:', err);
        // Force show login screen anyway
        showLoginScreen();
    }
}

/**
 * Show authenticated app UI
 */
function showAuthenticatedApp(user) {
    state.isAuthenticated = true;
    
    // Hide login/signup screens
    elements.loginScreen.classList.add('hidden');
    elements.signupScreen.classList.add('hidden');
    
    // Show main app
    elements.appMain.classList.remove('hidden');
    
    // Update user info
    if (elements.userEmail && user) {
        elements.userEmail.textContent = user.email || '';
    }
    
    // Setup event listeners for main app
    setupEventListeners();
    
    // Setup realtime subscriptions
    setupRealtimeSubscriptions();
    
    // Setup Page Visibility API
    setupVisibilityHandler();
    
    // Start stats polling
    state.statsPollInterval = setInterval(() => {
        if (state.isPageVisible) {
            loadAllStatsUnified();
        }
    }, CONFIG.STATS_POLL_INTERVAL);
    
    // Initial load
    refreshAll();
}

/**
 * Show login screen (when logged out or on initial load without session)
 */
function showLoginScreen() {
    state.isAuthenticated = false;
    
    // Clear intervals
    if (state.statsPollInterval) {
        clearInterval(state.statsPollInterval);
        state.statsPollInterval = null;
    }
    stopFallbackPolling();
    
    // Hide main app
    elements.appMain.classList.add('hidden');
    
    // Show login screen
    elements.loginScreen.classList.remove('hidden');
    elements.signupScreen.classList.add('hidden');
    
    // Clear form
    if (elements.loginEmail) elements.loginEmail.value = '';
    if (elements.loginPassword) elements.loginPassword.value = '';
    if (elements.loginError) elements.loginError.textContent = '';
    
    // Clear user info
    if (elements.userEmail) elements.userEmail.textContent = '';
}

/**
 * Called when site changes (from site-switcher.js)
 */
function onSiteChanged(siteId) {
    state.currentPage = 1;
    
    // Cleanup old subscriptions and setup new ones for the new site
    cleanupRealtimeSubscriptions();
    setupRealtimeSubscriptions();
    
    refreshAll();
}

// Export onSiteChanged for site-switcher.js
window.onSiteChanged = onSiteChanged;

function loadSettings() {
    // 1. Check for build-time environment variables (injected by esbuild)
    // We use a safe check for the existence of these variables
    const injectedUrl = typeof __SUPABASE_URL__ !== 'undefined' ? __SUPABASE_URL__ : '';
    const injectedKey = typeof __SUPABASE_ANON_KEY__ !== 'undefined' ? __SUPABASE_ANON_KEY__ : '';

    if (injectedUrl && injectedKey) {
        console.log('Using build-time Supabase configuration');
        elements.supabaseUrl.value = injectedUrl;
        elements.supabaseKey.value = injectedKey;
        initSupabaseAndCheckAuth(injectedUrl, injectedKey);
        return;
    }

    // 2. Fallback to localStorage (manual UI setup)
    const savedUrl = localStorage.getItem('supabase_url');
    const savedKey = localStorage.getItem('supabase_key');

    if (savedUrl && savedKey) {
        elements.supabaseUrl.value = savedUrl;
        elements.supabaseKey.value = savedKey;
        initSupabaseAndCheckAuth(savedUrl, savedKey);
    } else {
        elements.settingsModal.classList.remove('hidden');
    }
}

/**
 * Initialize Supabase and check for existing session
 */
function initSupabaseAndCheckAuth(url, key) {
    try {
        initSupabase(url, key);
        // Expose to window for other scripts (e.g., selector-manager.js)
        window.supabaseClient = getSupabase();

        elements.connectionStatus.textContent = '연결됨';
        elements.connectionStatus.className = 'connection-status connected';
        
        // Setup auth state change listener
        const supabase = getSupabase();
        supabase.onAuthStateChange((event, session) => {
            console.log('[Auth] State changed:', event);
            
            if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED_ERROR') {
                showLoginScreen();
            }
        });
        
        // Check for existing session
        if (supabase.isAuthenticated()) {
            const user = supabase.getUser();
            console.log('[Auth] Existing session found for:', user?.email);
            showAuthenticatedApp(user);
        } else {
            // No session - show login screen
            showLoginScreen();
        }
        
    } catch (error) {
        elements.connectionStatus.textContent = '연결 실패';
        elements.connectionStatus.className = 'connection-status disconnected';
        console.error('Connection failed:', error);
        showLoginScreen();
    }
}

// Keep old connectToSupabase for settings modal
function connectToSupabase(url, key) {
    initSupabaseAndCheckAuth(url, key);
}

/**
 * Setup Realtime subscriptions for tasks and workers
 */
function setupRealtimeSubscriptions() {
    const supabase = getSupabase();
    if (!supabase) return;
    
    // Connect to Realtime WebSocket
    supabase.connectRealtime();
    
    // Subscribe to tasks table changes
    const site = getCurrentSite();
    
    const tasksChannelId = supabase.subscribeToTable('tasks', site, (payload) => {
        console.log('[Realtime] Task change:', payload.type, payload.data?.id);
        handleTaskChange(payload);
    });
    state.realtimeChannels.push(tasksChannelId);
    
    // Subscribe to workers table changes
    const workersChannelId = supabase.subscribeToTable('workers', site, (payload) => {
        console.log('[Realtime] Worker change:', payload.type);
        // Debounce worker updates
        debouncedLoadWorkers();
    });
    state.realtimeChannels.push(workersChannelId);
    
    console.log('[Realtime] Subscriptions setup complete');
}

/**
 * Handle individual task changes from Realtime
 */
function handleTaskChange(payload) {
    const { type, data, old_record } = payload;
    const currentSite = getCurrentSite();
    
    // Only process if it's for current site
    if (data?.site !== currentSite && old_record?.site !== currentSite) {
        return;
    }
    
    switch (type) {
        case 'INSERT':
            // New task added - refresh stats and task list
            debouncedRefreshTasksAndStats();
            break;
            
        case 'UPDATE':
            // Task status changed
            const oldStatus = old_record?.status;
            const newStatus = data?.status;
            
            if (oldStatus !== newStatus) {
                // Status changed - update stats and relevant lists
                debouncedRefreshTasksAndStats();
                
                // If completed, also update gallery
                if (newStatus === 'completed') {
                    debouncedLoadGallery();
                }
            }
            break;
            
        case 'DELETE':
            // Task deleted - refresh everything
            debouncedRefreshTasksAndStats();
            break;
    }
}

/**
 * Cleanup Realtime subscriptions
 */
function cleanupRealtimeSubscriptions() {
    const supabase = getSupabase();
    if (!supabase) return;
    
    state.realtimeChannels.forEach(channelId => {
        supabase.unsubscribe(channelId);
    });
    state.realtimeChannels = [];
}

/**
 * Setup Page Visibility API handler
 */
function setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
        const supabase = getSupabase();
        
        if (document.hidden) {
            // Page hidden - pause realtime and stop polling
            state.isPageVisible = false;
            if (supabase) {
                supabase.pauseRealtime();
            }
            stopFallbackPolling();
            console.log('[Visibility] Page hidden - paused updates');
        } else {
            // Page visible - resume realtime and refresh
            state.isPageVisible = true;
            if (supabase) {
                supabase.resumeRealtime();
            }
            // Refresh data when page becomes visible again
            refreshAll();
            console.log('[Visibility] Page visible - resumed updates');
        }
    });
}

/**
 * Start fallback polling (when Realtime fails)
 */
function startFallbackPolling() {
    stopFallbackPolling();
    console.log('[Fallback] Starting polling mode');
    
    state.fallbackPollInterval = setInterval(() => {
        if (state.isPageVisible) {
            refreshAll();
        }
    }, CONFIG.FALLBACK_POLL_INTERVAL);
}

/**
 * Stop fallback polling
 */
function stopFallbackPolling() {
    if (state.fallbackPollInterval) {
        clearInterval(state.fallbackPollInterval);
        state.fallbackPollInterval = null;
    }
}

// Debounced functions to prevent excessive API calls
let refreshDebounceTimer = null;
let galleryDebounceTimer = null;
let workersDebounceTimer = null;

function debouncedRefreshTasksAndStats() {
    if (refreshDebounceTimer) clearTimeout(refreshDebounceTimer);
    refreshDebounceTimer = setTimeout(async () => {
        await Promise.all([loadAllStatsUnified(), loadTasks()]);
    }, CONFIG.DEBOUNCE_DELAY);
}

function debouncedLoadGallery() {
    if (galleryDebounceTimer) clearTimeout(galleryDebounceTimer);
    galleryDebounceTimer = setTimeout(() => {
        loadGallery();
    }, CONFIG.DEBOUNCE_DELAY);
}

function debouncedLoadWorkers() {
    if (workersDebounceTimer) clearTimeout(workersDebounceTimer);
    workersDebounceTimer = setTimeout(() => {
        loadWorkers();
    }, CONFIG.DEBOUNCE_DELAY);
}

function setupEventListeners() {
    // Settings
    elements.settingsBtn.addEventListener('click', () => {
        elements.settingsModal.classList.remove('hidden');
    });

    elements.closeModal.addEventListener('click', () => {
        elements.settingsModal.classList.add('hidden');
    });

    elements.saveSettings.addEventListener('click', () => {
        const url = elements.supabaseUrl.value.trim();
        const key = elements.supabaseKey.value.trim();

        if (!url || !key) {
            alert('URL과 Key를 모두 입력해주세요.');
            return;
        }

        localStorage.setItem('supabase_url', url);
        localStorage.setItem('supabase_key', key);
        connectToSupabase(url, key);
        elements.settingsModal.classList.add('hidden');
    });

    // Add task
    elements.addTaskBtn.addEventListener('click', addTasks);

    // Refresh
    elements.refreshBtn.addEventListener('click', refreshAll);

    // Filter
    elements.statusFilter.addEventListener('change', (e) => {
        state.statusFilter = e.target.value;
        state.currentPage = 1;
        loadTasks();
    });

    // Pagination
    elements.prevPage.addEventListener('click', () => {
        if (state.currentPage > 1) {
            state.currentPage--;
            loadTasks();
        }
    });

    elements.nextPage.addEventListener('click', () => {
        if (state.currentPage < state.totalPages) {
            state.currentPage++;
            loadTasks();
        }
    });

    // Download all
    elements.downloadAllBtn.addEventListener('click', downloadAllResults);
}

async function refreshAll() {
    if (!getSupabase()) return;

    await Promise.all([
        loadAllStatsUnified(),  // Combined stats load
        loadTasks(),
        loadWorkers(),
        loadGallery()
    ]);
}

/**
 * Load stats for all sites in a single query and update UI
 * Reduces 2 API calls to 1
 */
async function loadAllStatsUnified() {
    try {
        // Single query for all sites
        const data = await getSupabase().getAllSitesStats();
        const currentSite = getCurrentSite();
        
        if (Array.isArray(data)) {
            // Update badges for each site
            const allConfigs = getAllSiteConfigs();
            for (const siteId of Object.keys(allConfigs)) {
                const siteStats = data.find(s => s.site === siteId);
                const pendingCount = siteStats ? parseInt(siteStats.pending_count) || 0 : 0;
                updateSiteBadge(siteId, pendingCount);
            }
            
            // Update current site stats
            const currentStats = data.find(s => s.site === currentSite);
            if (currentStats) {
                state.stats = {
                    pending: parseInt(currentStats.pending_count) || 0,
                    claimed: parseInt(currentStats.claimed_count) || 0,
                    processing: parseInt(currentStats.processing_count) || 0,
                    completed: parseInt(currentStats.completed_count) || 0,
                    failed: parseInt(currentStats.failed_count) || 0
                };

                elements.statPending.textContent = state.stats.pending;
                elements.statClaimed.textContent = state.stats.claimed;
                elements.statProcessing.textContent = state.stats.processing;
                elements.statCompleted.textContent = state.stats.completed;
                elements.statFailed.textContent = state.stats.failed;
            }
        }
    } catch (error) {
        console.error('Failed to load stats:', error);
    }
}

/**
 * Load stats for current site only (for periodic updates)
 */
async function loadStats() {
    try {
        const site = getCurrentSite();
        const data = await getSupabase().getTaskStats(site);
        const stats = Array.isArray(data) ? data[0] : data;

        if (stats) {
            state.stats = {
                pending: parseInt(stats.pending_count) || 0,
                claimed: parseInt(stats.claimed_count) || 0,
                processing: parseInt(stats.processing_count) || 0,
                completed: parseInt(stats.completed_count) || 0,
                failed: parseInt(stats.failed_count) || 0
            };

            elements.statPending.textContent = state.stats.pending;
            elements.statClaimed.textContent = state.stats.claimed;
            elements.statProcessing.textContent = state.stats.processing;
            elements.statCompleted.textContent = state.stats.completed;
            elements.statFailed.textContent = state.stats.failed;
        }
    } catch (error) {
        console.error('Failed to load stats:', error);
    }
}

/**
 * Load stats for all sites (for tab badges)
 */
async function loadAllSitesStats() {
    try {
        const data = await getSupabase().getAllSitesStats();

        if (Array.isArray(data)) {
            // Update badges for each site
            const allConfigs = getAllSiteConfigs();
            for (const siteId of Object.keys(allConfigs)) {
                const siteStats = data.find(s => s.site === siteId);
                const pendingCount = siteStats ? parseInt(siteStats.pending_count) || 0 : 0;
                updateSiteBadge(siteId, pendingCount);
            }
        }
    } catch (error) {
        console.error('Failed to load all sites stats:', error);
    }
}

/**
 * Load tasks for current site
 */
async function loadTasks() {
    try {
        const site = getCurrentSite();
        const options = {
            select: '*',
            site: site,
            order: 'created_at.desc',
            limit: state.pageSize,
            offset: (state.currentPage - 1) * state.pageSize
        };

        if (state.statusFilter !== 'all') {
            options.filters = { status: `eq.${state.statusFilter}` };
        }

        const tasks = await getSupabase().query('tasks', options);

        if (tasks.length === 0) {
            elements.tasksList.innerHTML = '<p class="empty-state">작업이 없습니다</p>';
        } else {
            elements.tasksList.innerHTML = tasks.map(task => renderTaskItem(task)).join('');
        }

        // Update pagination
        state.totalPages = Math.max(1, Math.ceil(tasks.length / state.pageSize) || 1);
        elements.pageInfo.textContent = `${state.currentPage} / ${state.totalPages}`;
        elements.prevPage.disabled = state.currentPage <= 1;
        elements.nextPage.disabled = state.currentPage >= state.totalPages || tasks.length < state.pageSize;

    } catch (error) {
        console.error('Failed to load tasks:', error);
        elements.tasksList.innerHTML = '<p class="empty-state">작업을 불러오지 못했습니다</p>';
    }
}

/**
 * Load workers for current site
 */
async function loadWorkers() {
    try {
        const site = getCurrentSite();
        const workers = await getSupabase().query('workers', {
            select: '*',
            site: site,
            order: 'last_heartbeat.desc',
            limit: 10
        });

        // Update worker name cache for display in gallery/tasks
        if (typeof updateWorkerCache === 'function') {
            updateWorkerCache(workers);
        }

        if (workers.length === 0) {
            elements.workersList.innerHTML = '<p class="empty-state">활성 워커가 없습니다</p>';
        } else {
            elements.workersList.innerHTML = workers.map(worker => {
                const isOnline = new Date(worker.last_heartbeat) > new Date(Date.now() - 60000);
                const status = isOnline ? worker.status : 'offline';

                return `
                    <div class="worker-item">
                        <div class="worker-info">
                            <div class="worker-name">${worker.name || worker.id.substring(0, 8)}</div>
                            <div class="worker-stats">완료: ${worker.tasks_completed} | 실패: ${worker.tasks_failed}</div>
                        </div>
                        <span class="worker-status ${status}">${status}</span>
                    </div>
                `;
            }).join('');
        }
    } catch (error) {
        console.error('Failed to load workers:', error);
    }
}

/**
 * Load gallery for current site
 */
async function loadGallery() {
    try {
        const site = getCurrentSite();
        const tasks = await getSupabase().query('tasks', {
            select: '*',
            site: site,
            filters: { status: 'eq.completed' },
            order: 'completed_at.desc',
            limit: CONFIG.GALLERY_LIMIT
        });

        // Use site-switcher's renderGallery function
        renderGallery(tasks);
    } catch (error) {
        console.error('Failed to load gallery:', error);
    }
}

/**
 * Add tasks (site-specific form handling)
 */
async function addTasks() {
    const formData = getFormData();

    if (formData.error) {
        showResult(formData.error, 'error');
        return;
    }

    if (!formData.items || formData.items.length === 0) {
        showResult('추가할 작업이 없습니다.', 'error');
        return;
    }

    const site = getCurrentSite();
    const config = getSiteConfig();

    // Handle reference images upload for Genspark (up to 5)
    let referenceImageUrls = [];
    if (formData.referenceFiles && formData.referenceFiles.length > 0) {
        try {
            showResult(`참조 이미지 업로드 중... (0/${formData.referenceFiles.length})`, 'success');

            for (let i = 0; i < formData.referenceFiles.length; i++) {
                const file = formData.referenceFiles[i];
                const path = `${config.storagePath}/reference/shared/${Date.now()}_${i}_${file.name}`;
                await getSupabase().uploadFile('automation-files', path, file);
                const url = getSupabase().getPublicUrl('automation-files', path);
                referenceImageUrls.push(url);

                showResult(`참조 이미지 업로드 중... (${i + 1}/${formData.referenceFiles.length})`, 'success');
            }
        } catch (error) {
            showResult('참조 이미지 업로드 실패: ' + error.message, 'error');
            return;
        }
    }

    try {
        // Build task objects
        const tasks = formData.items.map(item => {
            const taskInput = { ...item.input };

            // Add reference image URLs for Genspark (array)
            if (referenceImageUrls.length > 0 && site === 'genspark') {
                taskInput.reference_image_urls = referenceImageUrls;
                // 하위 호환성을 위해 첫 번째 이미지도 저장
                taskInput.reference_image_url = referenceImageUrls[0];
            }

            return {
                site: site,
                input: taskInput,
                priority: item.priority
            };
        });

        await getSupabase().insert('tasks', tasks);

        showResult(`${tasks.length}개의 작업이 추가되었습니다.`, 'success');

        // Clear form
        if (formData.clear) {
            formData.clear();
        }

        refreshAll();
    } catch (error) {
        showResult('추가 실패: ' + error.message, 'error');
    }
}

/**
 * Download all completed results
 */
async function downloadAllResults() {
    const site = getCurrentSite();
    const config = getSiteConfig();

    try {
        const tasks = await getSupabase().query('tasks', {
            select: '*',
            site: site,
            filters: { status: 'eq.completed' }
        });

        if (tasks.length === 0) {
            alert('다운로드할 결과가 없습니다.');
            return;
        }

        // Download based on output type
        if (config.outputType === 'image') {
            for (const task of tasks) {
                const url = task.output?.image_url;
                if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '';
                    link.target = '_blank';
                    link.click();
                    await new Promise(r => setTimeout(r, 500));
                }
            }
        } else if (config.outputType === 'audio') {
            for (const task of tasks) {
                const url = task.output?.audio_url;
                if (url) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '';
                    link.target = '_blank';
                    link.click();
                    await new Promise(r => setTimeout(r, 500));
                }
            }
        } else if (config.outputType === 'link') {
            // For links, create a text file with all URLs
            const urls = tasks
                .map(t => `${t.input?.title || 'Untitled'}: ${t.output?.post_url || ''}`)
                .filter(line => line.includes('http'))
                .join('\n');

            const blob = new Blob([urls], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${site}_completed_links.txt`;
            link.click();
            URL.revokeObjectURL(url);
        }
    } catch (error) {
        alert('다운로드 실패: ' + error.message);
    }
}

function showResult(message, type) {
    elements.addResult.textContent = message;
    elements.addResult.className = `result-message ${type}`;
    setTimeout(() => {
        elements.addResult.textContent = '';
        elements.addResult.className = 'result-message';
    }, 5000);
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return '방금 전';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}분 전`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}시간 전`;

    return date.toLocaleDateString('ko-KR');
}

/**
 * Reset a single task back to pending (global function for onclick)
 */
async function resetTask(taskId, site) {
    if (!confirm('이 작업을 재시도 대기열로 되돌리시겠습니까?')) {
        return;
    }

    try {
        await getSupabase().resetTask(taskId, site);
        showResult('작업이 재시도 대기열로 이동되었습니다.', 'success');
        refreshAll();
    } catch (error) {
        showResult('복구 실패: ' + error.message, 'error');
    }
}

/**
 * Force recover all stuck tasks for current site
 */
async function forceRecoverAllTasks() {
    const site = getCurrentSite();

    if (!confirm(`${site}의 모든 멈춘 작업(할당됨/처리중)을 복구하시겠습니까?`)) {
        return;
    }

    try {
        const count = await getSupabase().forceRecoverStuckTasks(site);
        showResult(`${count}개의 작업이 복구되었습니다.`, 'success');
        refreshAll();
    } catch (error) {
        showResult('복구 실패: ' + error.message, 'error');
    }
}

/**
 * Try to recover image from Genspark agent page
 * Opens agent page in new tab and tries to extract image
 */
async function tryRecoverTask(taskId, site, agentId) {
    if (!agentId) {
        showResult('복구할 Agent ID가 없습니다.', 'error');
        return;
    }

    const agentUrl = `https://www.genspark.ai/agents?id=${agentId}`;

    // Open agent page in new window for manual check
    const confirmed = confirm(
        `Agent 페이지를 열어 이미지 복구를 시도합니다.\n\n` +
        `URL: ${agentUrl}\n\n` +
        `새 탭에서 페이지를 열고 이미지가 생성되었는지 확인하세요.\n` +
        `이미지가 있다면 수동으로 저장하거나, 확장프로그램이 자동 복구를 시도합니다.`
    );

    if (confirmed) {
        window.open(agentUrl, '_blank');
        showResult('Agent 페이지가 열렸습니다. 이미지를 확인하세요.', 'success');
    }
}

/**
 * Open retry modal with task data
 * @param {string} taskDataBase64 - Base64 encoded JSON string with prompt and reference_image_urls
 */
function openRetryModal(taskDataBase64) {
    try {
        // Decode base64 and parse JSON
        const taskDataJson = decodeURIComponent(atob(taskDataBase64));
        const taskData = JSON.parse(taskDataJson);

        const modal = document.getElementById('retry-modal');
        const promptInput = document.getElementById('retry-prompt');
        const referenceImagesDiv = document.getElementById('retry-reference-images');
        const referenceUrlsInput = document.getElementById('retry-reference-urls');

        // Set prompt
        promptInput.value = taskData.prompt || '';

        // Store reference URLs in hidden input
        const referenceUrls = taskData.reference_image_urls || [];
        referenceUrlsInput.value = JSON.stringify(referenceUrls);

        // Render reference images preview
        if (referenceUrls.length > 0) {
            referenceImagesDiv.innerHTML = referenceUrls.map(url =>
                `<img src="${escapeHtml(url)}" alt="참조 이미지" loading="lazy">`
            ).join('');
        } else {
            referenceImagesDiv.innerHTML = '<span class="no-images">참조 이미지 없음</span>';
        }

        // Show modal
        modal.classList.remove('hidden');
    } catch (error) {
        console.error('Failed to open retry modal:', error);
        showResult('재시도 모달 열기 실패', 'error');
    }
}

/**
 * Close retry modal
 */
function closeRetryModal() {
    const modal = document.getElementById('retry-modal');
    modal.classList.add('hidden');
}

/**
 * Submit retry task with modified prompt and same reference images
 */
async function submitRetryTask() {
    const promptInput = document.getElementById('retry-prompt');
    const referenceUrlsInput = document.getElementById('retry-reference-urls');
    const prioritySelect = document.getElementById('retry-priority');

    const prompt = promptInput.value.trim();
    if (!prompt) {
        showResult('프롬프트를 입력해주세요.', 'error');
        return;
    }

    let referenceUrls = [];
    try {
        referenceUrls = JSON.parse(referenceUrlsInput.value || '[]');
    } catch (e) {
        referenceUrls = [];
    }

    const priority = parseInt(prioritySelect.value) || 0;
    const site = getCurrentSite();

    try {
        const taskInput = { prompt };

        // Add reference image URLs if available
        if (referenceUrls.length > 0) {
            taskInput.reference_image_urls = referenceUrls;
            taskInput.reference_image_url = referenceUrls[0]; // 하위 호환성
        }

        await getSupabase().insert('tasks', [{
            site: site,
            input: taskInput,
            priority: priority
        }]);

        showResult('재시도 작업이 추가되었습니다.', 'success');
        closeRetryModal();
        refreshAll();
    } catch (error) {
        showResult('작업 추가 실패: ' + error.message, 'error');
    }
}

// Setup retry modal event listeners
document.addEventListener('DOMContentLoaded', () => {
    // Close retry modal button
    const closeRetryBtn = document.getElementById('close-retry-modal');
    if (closeRetryBtn) {
        closeRetryBtn.addEventListener('click', closeRetryModal);
    }

    // Close on backdrop click
    const retryModal = document.getElementById('retry-modal');
    if (retryModal) {
        retryModal.addEventListener('click', (e) => {
            if (e.target.id === 'retry-modal') {
                closeRetryModal();
            }
        });
    }

    // Submit retry button
    const submitRetryBtn = document.getElementById('submit-retry');
    if (submitRetryBtn) {
        submitRetryBtn.addEventListener('click', submitRetryTask);
    }
});
